import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { filter, map, take, tap } from "rxjs/operators";
import { InitService } from "@core-services/init.service";
import { ProfileRemote } from "@modules/main/components/profile/new-profile/infrastructure/new-profile.remote";
import { ServiceNames } from "@models/service_enums";
import { NewProfileStateService } from "@modules/main/components/profile/new-profile/state/new-profile.state.service";
import * as moment from "moment/moment";
import { getName } from "@modules/main/components/profile/new-profile/new-profile-utils";
import { ProfileModel } from "@modules/main/components/profile/new-profile/entities/profile-model";
import { ChangeUserInfoNavigationService } from "@modules/shared/modals/change-personal-info/domain/infrastructure/change-user-info-navigation.service";
import { UserInfoRouteNames } from "@modules/shared/modals/change-personal-info/domain/entities/change-user-info.model";
import { UpdateInfoUserType } from "@modules/shared/modals/change-personal-info/constants/user-info-constants.model";
import { TranslationService } from "@core-services/translation.service";
import { Params } from "@angular/router";
import { LocationModel } from "@modules/new_account/features/new_account-profile/components/new_account-profile-container.component";
import { CountriesService } from "@core-services/countries.service";

@Injectable({ providedIn: "root" })
export class NewProfileFacade {
	public name: string = ServiceNames.PROFILE;

	public constructor(
		private initService: InitService,
		private profileRemote: ProfileRemote,
		private profileStateService: NewProfileStateService,
		private readonly translationService: TranslationService,
		private readonly navigationService: ChangeUserInfoNavigationService,
		private readonly countriesService: CountriesService
	) {}

	public getLocations$: Observable<LocationModel> = this.getProfile$().pipe(
		map((profile) => ({
			country: this.getCountryAndCitizenship(profile.country_id),
			citizenship: this.getCountryAndCitizenship(profile.citizenship_id)
		}))
	);

	public fetchProfile(): void {
		this.profileRemote
			.getProfile$()
			.pipe(filter((profile) => profile !== null))
			.subscribe((profile) => {
				this.initService.init(this.name);
				this.profileStateService.updateState(
					profile,
					getName(profile), // fullname
					moment.parseZone(profile.birthday.getDate()).format("DD.MM.YYYY"), //fullbirthday
					profile.profile_id // id for braze,
				);
			});
	}

	public getProfile$(): Observable<ProfileModel> {
		return this.profileStateService.getState$().pipe(filter((state) => !!state));
	}

	public update(profile: ProfileModel): Observable<ProfileModel> {
		return this.profileRemote
			.postProfile$(profile)
			.pipe(
				tap((profile) =>
					this.profileStateService.updateState(
						profile,
						getName(profile),
						moment.parseZone(profile.birthday.getDate()).format("DD.MM.YYYY"),
						profile.profile_id
					)
				)
			);
	}

	public updatePhone(phoneNumber: string): void {
		this.profileStateService.updatePhone(phoneNumber);
	}

	public setPhoneNumberVerified(b: boolean): void {
		this.profileStateService.setPhoneNumberVerified(b);
	}

	public setAcceptedTermsData(b: boolean): void {
		this.profileStateService.setAcceptedTermsData(b);
	}

	public setAcceptedTermVerification(b: boolean): void {
		this.profileStateService.setAcceptedTermsVerification(b);
	}

	public navigateToPath(path: UserInfoRouteNames): void {
		this.navigationService.gotoByNavigationUrl(path);
	}

	public navigateToPathAndQp(path: UserInfoRouteNames, qp: Params): void {
		this.navigationService.updateQpAndNavigate(path, qp);
	}

	public goToHelpDesk(dataType: UpdateInfoUserType): void {
		const helpdeskLink = "/new?ticket_form_id=9052443718172&deeplink=";
		const url = this.translationService.getHdLink("/requests") + helpdeskLink + dataType;
		window.open(url, "_blank");
	}

	public getCountryAndCitizenship(countryCode: string): string {
		let countryName = "";
		this.countriesService.countries$.pipe(take(1)).subscribe((countries) => {
			const country = countries.find((country) => country.id === countryCode);
			countryName = country ? country.attributes.name : "";
		});
		return countryName;
	}

	public setTenancyTermsVerification(b: boolean): void {
		this.profileStateService.setTenancyTermsVerification(b);
	}

	public isUkResident$(): Observable<boolean> {
		return this.profileStateService.getState$().pipe(map((state) => !!state && state.country_id === "225"));
	}
}
